// src/oura/components/newscan/DefRow.tsx
//
// One fact in a New scan definition list: the label on the left as a bold,
// black title with what the row means under it (`hint`), the value on the
// right, hairline rows, stacked on a phone (Jovi, 20 Oct: the light label and
// the hints under each control read as clutter). One spacing for every
// step's list, so the lists line up.

import type { ReactNode } from "react";
import { cn } from "@oura/lib/cn";
import { TYPE } from "@oura/ui";

export interface DefRowProps {
    label: ReactNode;
    /** The dt's id, for a control labelled by it (aria-labelledby). */
    labelId?: string;
    /** The id of the control the label names (a <label htmlFor>). */
    labelFor?: string;
    /** What the row means, under the title in the label column. Give it an id when a control is described by it. */
    hint?: ReactNode;
    /** The hint's id, for a control's aria-describedby. */
    hintId?: string;
    /** Extra classes on the dd (the value column). */
    ddClassName?: string;
    children: ReactNode;
}

/** The label column's width, shared with lists that line up with this one. */
export const DEF_LABEL_COLUMN = "sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]";

export function DefRow({ label, labelId, labelFor, hint, hintId, ddClassName, children }: DefRowProps) {
    return (
        <div
            className={cn(
                "grid grid-cols-1 gap-x-6 gap-y-2 border-t border-gray-100 py-4 first:border-t-0 first:pt-0 last:pb-0",
                DEF_LABEL_COLUMN,
            )}
        >
            <dt className="min-w-0 sm:pt-1">
                <span id={labelId} className={cn(TYPE.tb, "block font-semibold text-gray-900")}>
                    {labelFor ? <label htmlFor={labelFor}>{label}</label> : label}
                </span>
                {hint && (
                    <span id={hintId} className={cn(TYPE.tc, "mt-1 block text-gray-500")}>
                        {hint}
                    </span>
                )}
            </dt>
            <dd className={cn("m-0 min-w-0 space-y-2", ddClassName)}>{children}</dd>
        </div>
    );
}
